import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Communication, Reference } from '@medplum/fhirtypes';
import { useCallback, useMemo } from 'react';
import {
  buildQuotedMessageSnapshot,
  createThread as createThreadFhir,
  sendMessageToThread,
  uploadAttachment,
} from '../services/messagingFhir';
import { resolveMessagingCurrentUserRef } from '../services/messagingIdentity';

export interface SendMessageParams {
  threadId: string;
  content: string;
  files: File[];
  parentMessageId?: string | null;
  quotedMessage?: Communication | null;
  threadRecipients?: Reference[];
}

export interface CreateThreadParams {
  recipients: Reference[];
  message: string;
  categoryCode: string;
  categoryDisplay: string;
  topicText?: string;
  subjectRef?: Reference;
  /** Space auto-membership rules (spaces only). Stored on the Group resource. */
  spaceRules?: Array<{ type: string; value: string }>;
  /** Practice tag code for compartment scoping. */
  practiceTagCode?: string;
  /** Organization reference for Group.managingEntity. */
  managingEntity?: Reference;
}

/**
 * Provides messaging mutation functions.
 * Creates resources on Medplum and uploads attachments.
 */
export function useMessagingActions(): {
  sendMessage: (params: SendMessageParams) => Promise<Communication>;
  createThread: (params: CreateThreadParams) => Promise<{ thread: Communication; message: Communication }>;
  currentUserRef: Reference;
  isMock: boolean;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const currentUserRef = useMemo((): Reference => {
    return resolveMessagingCurrentUserRef(profile, false);
  }, [profile]);

  const sendMessage = useCallback(
    async (params: SendMessageParams): Promise<Communication> => {
      const { threadId, content, files, parentMessageId, quotedMessage, threadRecipients } = params;

      const payloads: NonNullable<Communication['payload']> = [];
      if (content.trim()) {
        payloads.push({ contentString: content });
      }

      if (files.length > 0) {
        for (const file of files) {
          const p = await uploadAttachment(
            medplum,
            file,
            { reference: `Communication/${threadId}` },
          );
          payloads.push(p);
        }
      }

      const quotedSnapshot = !parentMessageId && quotedMessage
        ? buildQuotedMessageSnapshot(quotedMessage)
        : undefined;

      return sendMessageToThread(medplum, {
        threadId,
        senderRef: currentUserRef,
        recipients: threadRecipients ?? [],
        content,
        attachmentPayloads: payloads.filter((p) => p.contentAttachment),
        parentMessageId: parentMessageId ?? undefined,
        quotedMessage: quotedSnapshot,
      });
    },
    [medplum, currentUserRef],
  );

  const doCreateThread = useCallback(
    async (params: CreateThreadParams): Promise<{ thread: Communication; message: Communication }> => {
      const result = await createThreadFhir(medplum, {
        senderRef: currentUserRef,
        recipients: params.recipients,
        categoryCode: params.categoryCode,
        categoryDisplay: params.categoryDisplay,
        topicText: params.topicText,
        initialMessage: params.message,
        practiceTagCode: params.practiceTagCode,
        spaceRules: params.spaceRules,
        managingEntity: params.managingEntity,
      });
      return { thread: result.thread, message: result.message };
    },
    [medplum, currentUserRef],
  );

  return { sendMessage, createThread: doCreateThread, currentUserRef, isMock: false };
}
